import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Polygon, Popup } from 'react-leaflet';
import MapTouchGate from '../../components/map/MapTouchGate';
import 'leaflet/dist/leaflet.css';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { Map as MapIcon, Loader2, AlertCircle, ChevronLeft, Satellite, Map as StreetIcon } from 'lucide-react';
import breederService, { type PlantingsMapResponse } from '../../services/breeder';
import { geoJSONToLeaflet, TILE_LAYERS, type TileLayerKey } from '../../utils/leafletGeo';

// Cyclic palette for years (deterministic by year mod length).
const YEAR_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#17becf', '#bcbd22', '#7f7f7f',
];

function colorForYear(year: number | null): string {
  if (!year) return '#999';
  return YEAR_COLORS[year % YEAR_COLORS.length];
}

function BreederPlantingMap() {
  const { id } = useParams<{ id: string }>();
  const cultivarId = id ? Number(id) : null;

  const [data, setData] = useState<PlantingsMapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tileLayer, setTileLayer] = useState<TileLayerKey>('satellite');

  useEffect(() => {
    if (!cultivarId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await breederService.getPlantingsMap(cultivarId);
        if (cancelled) return;
        if (res.success && res.data) setData(res.data);
        else setError(res.message || 'Failed to load planting map');
      } catch (err) {
        if (cancelled) return;
        const msg = (err as { message?: string } | null)?.message
          || 'Failed to load planting map';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cultivarId]);

  const polygons = useMemo(() => {
    if (!data) return [];
    return data.plantings
      .filter(p => p.boundary_geometry && p.boundary_geometry.coordinates?.length > 0)
      .map(p => ({
        area_id: p.area_id,
        positions: geoJSONToLeaflet(p.boundary_geometry!.coordinates),
        planted_year: p.planted_year,
        tree_count: p.tree_count,
        area_hectares: Number(p.area_hectares),
        color: colorForYear(p.planted_year),
      }));
  }, [data]);

  const center = useMemo<[number, number]>(() => {
    if (polygons.length === 0) return [-37.5, 144.5]; // Victoria fallback
    let latSum = 0, lngSum = 0, n = 0;
    for (const p of polygons) {
      for (const [lat, lng] of p.positions) { latSum += lat; lngSum += lng; n += 1; }
    }
    return n > 0 ? [latSum / n, lngSum / n] : [-37.5, 144.5];
  }, [polygons]);

  const years = data ? Object.keys(data.by_year).sort() : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Plantings — anonymised"
        subtitle="Polygons by planting year. Farm and grower identities are intentionally not shown."
        icon={<MapIcon className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4">
          <Link to="/breeder">
            <Button variant="outline" size="sm">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back to dashboard
            </Button>
          </Link>
        </div>

        {loading ? (
          <Card><CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-3" />
            <p className="text-neutral-600">Loading planting map…</p>
          </CardContent></Card>
        ) : error ? (
          <Card><CardContent className="p-12 text-center">
            <AlertCircle className="h-10 w-10 text-error-600 mx-auto mb-3" />
            <p className="text-error-700">{error}</p>
          </CardContent></Card>
        ) : data ? (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
            <Card>
              <CardContent className="p-0 overflow-hidden">
                <div style={{ height: '70vh', width: '100%' }}>
                  <MapContainer center={center} zoom={polygons.length ? 10 : 6} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                      attribution={TILE_LAYERS[tileLayer].attribution}
                      url={TILE_LAYERS[tileLayer].url}
                    />
                    {polygons.map(p => (
                      <Polygon
                        key={p.area_id}
                        positions={p.positions}
                        pathOptions={{ color: p.color, fillColor: p.color, fillOpacity: 0.35, weight: 2 }}
                      >
                        <Popup>
                          <div className="text-sm">
                            <div><span className="font-medium">Year:</span> {p.planted_year ?? 'unknown'}</div>
                            <div><span className="font-medium">Trees:</span> {p.tree_count.toLocaleString()}</div>
                            <div><span className="font-medium">Area:</span> {p.area_hectares.toFixed(2)} ha</div>
                          </div>
                        </Popup>
                      </Polygon>
                    ))}
                    <MapTouchGate />
                  </MapContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-neutral-900">By year</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTileLayer(t => t === 'satellite' ? 'street' : 'satellite')}
                    title="Toggle map layer"
                  >
                    {tileLayer === 'satellite'
                      ? <><StreetIcon className="h-3.5 w-3.5 mr-1" />Street</>
                      : <><Satellite className="h-3.5 w-3.5 mr-1" />Satellite</>}
                  </Button>
                </div>
                {years.length === 0 ? (
                  <p className="text-sm text-neutral-500">No planting data with polygons.</p>
                ) : (
                  <ul className="space-y-2">
                    {years.map(y => {
                      const yearNum = y === 'unknown' ? null : Number(y);
                      const s = data.by_year[y];
                      return (
                        <li key={y} className="flex items-center gap-2 text-sm">
                          <span
                            className="inline-block w-3 h-3 rounded-sm"
                            style={{ backgroundColor: colorForYear(yearNum) }}
                          />
                          <span className="font-mono w-12">{y}</span>
                          <span className="text-neutral-600">
                            {s.plantings} {s.plantings === 1 ? 'planting' : 'plantings'} · {s.trees.toLocaleString()} trees · {s.hectares.toFixed(1)} ha
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="mt-6 pt-4 border-t border-neutral-200 text-xs text-neutral-500">
                  <p className="font-medium mb-1">Privacy</p>
                  <p>Polygon shapes are real but farm names, block names, and grower contact details are stripped. Use this view for territory and reach analysis only.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default BreederPlantingMap;
